import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { AskRequest, AskRequestCreateType, HoneAbilityActions, HoneAbilitySubjects } from '@hone-automation/common';
import { MessageList } from './components/MessageList';
import { Header } from './components/Header';
import { AskRequestQueueComment } from '../../Cards/MessagesCard/messagesCardFactory';
import CMComposer, { CMComposerRef } from 'presentation/components/Queue/CMComposer/CMComposer';
import { CMComposerContexts } from 'presentation/components/Queue/CMComposer/types';
import { IconQuestion, IconBack } from 'components/Icons';
import { PendingMessageData } from './components/MessageList/PendingMessage';
import { useAuthContext } from 'context/useAuthContext';
import { UIReportTransaction } from 'types/askcustomer';
import { DataCellState } from 'domain/models';
import { useParams } from 'react-router-dom';
import { useQueueRequestsQuery } from 'hooks/useQueueRequestsQuery';
import { ConversationPanelContextProvider, ConversationMode } from './ConversationPanelContext';
import type { ConversationSidePanel } from './ConversationPanelContext';
import { currencyFormatter2Decimals } from 'lib/utils';
import './ConversationPanel.scss';

interface ConversationPanelProps {
  /** The AskRequest - optional for new flagged transactions that don't have a request yet */
  request?: AskRequest;
  comments?: AskRequestQueueComment[];
  mobile?: boolean;
  // New props for DataRowModal context
  context?: ConversationMode;
  transaction?: UIReportTransaction;
  onTransactionUpdate?: (transaction: UIReportTransaction) => void;
  modalState?: DataCellState;
  reportId?: string;
  messageLoading?: boolean;
  /** Callback for handling form submission in DataRowModal context */
  handleOnCallback?: (formData: FormData, createRequestType: AskRequestCreateType) => void;
  /** Optional callback to return to list (used on compact layouts) */
  onBackToList?: () => void;
  /** Optional callback to open a side panel in DataRowModal */
  onOpenSidePanel?: (panel: ConversationSidePanel) => void;
  /** Auto-focus the composer when mounted */
  autoFocusComposer?: boolean;
}

/**
 * ConversationPanel - Right-side details panel for Queue items
 *
 * Vertical flex layout with three rows:
 * - Header (top) - actions and controls with gradient overlay
 * - MessageList (middle) - conversation view (fills available space)
 * - CMComposer (bottom) - message composer
 *
 * Can operate in two modes:
 * - 'queue': Used within KSQ2 Queue with QueueContext available
 * - 'dataRowModal': Used within FlagCreationPanel without QueueContext
 */
function ConversationPanel({
  request,
  comments,
  mobile = false,
  context = 'queue',
  transaction,
  onTransactionUpdate,
  modalState,
  reportId,
  messageLoading = false,
  handleOnCallback,
  onBackToList,
  onOpenSidePanel,
  autoFocusComposer = false,
}: ConversationPanelProps) {
  const composerRef = useRef<CMComposerRef>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<PendingMessageData | null>(null);
  const dragCounterRef = useRef(0);
  const prevCommentsLengthRef = useRef(comments?.length ?? 0);

  // Get locationId from URL params as fallback
  const { locationId: urlLocationId } = useParams();
  // Use resolved location ID from modalState for multi-location reports, fallback to URL location
  const effectiveLocationId = modalState?.resolvedLocationId || urlLocationId;

  // Get refetch function for updating flaggedTransactions in DataRowModal mode
  const { refetch: refetchFlaggedTransactions } = useQueueRequestsQuery(undefined, false, false);

  // second phase is to be deleted when we have integrated from the BE the assignment and due-date
  const secondPhase = true;

  const { currentLocationAbility } = useAuthContext();
  const canManageQueue = currentLocationAbility?.can?.(HoneAbilityActions.manage, HoneAbilitySubjects.Queue) ?? false;

  const cannotReopen = request?.status === 'Closed' && !request?.canReopen;

  // Build context value for ConversationPanelContext
  const conversationPanelContextValue = useMemo(
    () => ({
      mode: context,
      locationId: effectiveLocationId,
      messageLoading,
      reportId,
      onRequestUpdate: (updatedRequest: AskRequest) => {
        if (context === 'dataRowModal' && onTransactionUpdate && transaction) {
          onTransactionUpdate({
            ...transaction,
            flagged: updatedRequest,
          });
          refetchFlaggedTransactions();
        }
      },
      openSidePanel: onOpenSidePanel,
      onTransactionUpdate,
      refetchFlaggedTransactions,
    }),
    [
      context,
      effectiveLocationId,
      messageLoading,
      reportId,
      onOpenSidePanel,
      onTransactionUpdate,
      transaction,
      refetchFlaggedTransactions,
    ]
  );

  // Clear pending message when new comment arrives or request changes
  useEffect(() => {
    const currentLength = comments?.length ?? 0;
    if (currentLength > prevCommentsLengthRef.current && pendingMessage) {
      setPendingMessage(null);
    }
    prevCommentsLengthRef.current = currentLength;
  }, [comments?.length, pendingMessage]);

  // Clear pending message when request changes (e.g., moved to different column)
  useEffect(() => {
    setPendingMessage(null);
  }, [request?.id]);

  // Handler for when composer starts sending
  const handleMessageSending = useCallback((content: string) => {
    setPendingMessage({
      content,
      timestamp: new Date(),
    });
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounterRef.current = 0;

      if (cannotReopen) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        // Add first file to the composer
        composerRef.current?.addFile(files[0]);
      }
    },
    [cannotReopen]
  );

  // Determine CMComposer context based on mode
  const composerContext = context === 'dataRowModal' ? CMComposerContexts.flaggedCell : CMComposerContexts.queue;

  // Calculate amount for display in DataRowModal context
  const displayAmount = useMemo(() => {
    if (context !== 'dataRowModal') return undefined;

    // Try to get amount from transaction or request
    const amount =
      transaction?.amount || (transaction as any)?.transaction?.amount || request?.flaggedAmount || modalState?.total;

    if (amount !== undefined && amount !== null) {
      return currencyFormatter2Decimals(amount);
    }
    return undefined;
  }, [context, transaction, request?.flaggedAmount, modalState?.total]);

  return (
    <ConversationPanelContextProvider value={conversationPanelContextValue}>
      <div
        className={`conversation-panel ${mobile ? 'mobile' : ''} ${isDragging ? 'dragging' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {onBackToList && (
          <button
            type="button"
            className="conversation-panel__back-button"
            onClick={onBackToList}
            aria-label="Back to list"
          >
            <IconBack />
            <span>Back to list</span>
          </button>
        )}
        {request && (
          <Header
            request={request}
            secondPhase={secondPhase}
            canManageQueue={canManageQueue}
            amount={displayAmount}
            showStatusLabel={context === 'dataRowModal'}
          />
        )}
        <MessageList
          request={request}
          comments={comments}
          mobile={mobile}
          fixedHeight
          pendingMessage={pendingMessage}
        />
        <div className="conversation-panel__composer">
          {cannotReopen ? (
            <div className="conversation-panel__cannot-reopen">
              <IconQuestion />
              <span>This request cannot be reopened. Please check your current reports to flag new issues.</span>
            </div>
          ) : (
            <div className="conversation-panel__composer-gradient-base">
              <CMComposer
                ref={composerRef}
                request={request!}
                context={composerContext}
                onMessageSending={handleMessageSending}
                handleOnCallback={handleOnCallback}
                messageLoading={messageLoading}
                overrideLocationId={context === 'dataRowModal' ? effectiveLocationId : undefined}
                autoFocus={autoFocusComposer}
                isRightPanelComposer
              />
              <div className="conversation-panel__composer-gradient-base__warning">
                <b>Kitchensync AI</b> can make mistakes. Please double-check responses.
              </div>
            </div>
          )}
        </div>
        {isDragging && !cannotReopen && (
          <div className="conversation-panel__drop-overlay">
            <div className="conversation-panel__drop-message">Drop file to attach (one at a time)</div>
          </div>
        )}
      </div>
    </ConversationPanelContextProvider>
  );
}

export default ConversationPanel;
